import { useEffect, useState } from 'react'

/**
 * Banner persistente exibido enquanto admin está logado como cliente.
 *
 * Como funciona:
 * - Admin abre /?impersonate=<jwt> em nova aba (ver App.tsx)
 * - O JWT carrega claim impersonatedBy: { adminId, adminEmail, at }
 * - Este banner decodifica o payload do JWT (sem verificar — só pra display)
 *   e mostra: quem você é vendo, qual admin entrou, quanto tempo resta.
 * - Sair = remove pakt_token e pakt_user, fecha a aba.
 */

interface ImpersonationClaims {
  adminEmail: string
  at: string
}

function decodeJwtPayload(token: string): {
  exp?: number
  impersonatedBy?: ImpersonationClaims
  email?: string
} | null {
  try {
    const parts = token.split('.')
    if (parts.length !== 3) return null
    // base64url → base64
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    const padded = b64 + '==='.slice((b64.length + 3) % 4)
    return JSON.parse(atob(padded))
  } catch {
    return null
  }
}

export default function ImpersonationBanner() {
  const [now, setNow] = useState(Date.now())

  // Refresh a cada 10s pra atualizar o countdown
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 10_000)
    return () => clearInterval(t)
  }, [])

  const token = localStorage.getItem('pakt_token')
  if (!token) return null

  const payload = decodeJwtPayload(token)
  if (!payload?.impersonatedBy) return null

  // Token expirou: força sair (limpa storage + recarrega pra cair no login)
  if (payload.exp && payload.exp * 1000 < now) {
    localStorage.removeItem('pakt_token')
    localStorage.removeItem('pakt_user')
    window.location.href = '/login'
    return null
  }

  const minutesLeft = payload.exp
    ? Math.max(0, Math.ceil((payload.exp * 1000 - now) / 60_000))
    : null

  function exit() {
    localStorage.removeItem('pakt_token')
    localStorage.removeItem('pakt_user')
    // Tenta fechar a aba; se não der (não foi aberta por script), redireciona.
    window.close()
    setTimeout(() => { window.location.href = '/login' }, 200)
  }

  return (
    <div className="sticky top-0 z-50 bg-pakt-orange text-white text-sm">
      <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between gap-4">
        <div className="font-mono">
          <span className="uppercase tracking-wider text-xs opacity-90">Modo impersonação · só leitura</span>
          <span className="mx-2 opacity-60">·</span>
          <span>vendo como <strong>{payload.email}</strong></span>
          <span className="mx-2 opacity-60">·</span>
          <span>admin: {payload.impersonatedBy.adminEmail}</span>
          {minutesLeft !== null && (
            <>
              <span className="mx-2 opacity-60">·</span>
              <span className="tabular-nums">expira em {minutesLeft}min</span>
            </>
          )}
        </div>
        <button
          onClick={exit}
          className="bg-white text-pakt-orange px-3 py-1 text-xs font-mono uppercase tracking-wider hover:bg-opacity-90"
        >
          Sair
        </button>
      </div>
    </div>
  )
}
